import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Avatar,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  CircularProgress,
  Alert,
  LinearProgress,
  ButtonGroup,
  IconButton,
  Tooltip,
  Pagination,
  InputAdornment,
  Collapse,
  useTheme,
  useMediaQuery,
  styled
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Check as AcceptIcon,
  Clear as RejectIcon,
  Done as CompleteIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Download as DownloadIcon,
  FilterAlt as FilterAltIcon,
  Add as AddIcon,
  List as ListIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  ForwardToInbox as ForwardToInboxIcon,
  Backspace as ClearIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import LocalizedDatePicker from '../../components/common/LocalizedDatePicker';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import useFetchObject from '../../api/useFetchObject';
import useFetchObjects from '../../api/useFetchObjects';
import useUpdate from '../../api/useUpdate';
import useAdd from '../../api/useAdd';
import useDelete from '../../api/useDelete';
import TaskAssignForm from '../../components/tasks/TaskAssignForm';
import DocumentSelectionDialog from '../../components/tasks/DocumentSelectionDialog';
import BulkAssignForm from '../../components/tasks/BulkAssignForm';
import { useLazyUsers } from '../../hooks/useLazyUsers';

// Import extracted components
import TaskCard from '../../components/tasks/TaskCard';
import TaskDetailDialog from '../../components/tasks/TaskDetailDialogV2';
import TaskStatistics from '../../components/tasks/TaskStatistics';
import TaskFilters from '../../components/tasks/TaskFilters';
import { RejectDialog, ApproveDialog } from '../../components/tasks/TaskDialogs';

// Import task handlers
import { 
  createTaskAcceptHandler, 
  createTaskRejectHandler, 
  createTaskApproveHandler,
  safeFormatDate,
  isTaskInProgress
} from '../../components/tasks/TaskHandlers';

// Delete confirm dialog hook
// Import our new components
import CalendarSelector from '../../components/common/CalendarSelector';
import LocalizedDate from '../../components/common/LocalizedDate';
import { useDateFormat } from '../../context/DateFormatContext';
import { RTLTextField, RTLFormControl, RTLButton } from '../../styles/rtlComponents';
import useRTL from '../../hooks/useRTL';

// Using centralized RTL components from styles/rtlComponents.js

// RTL-aware TableCell component with bold header option
const RTLTableCell = styled(Box)(({ theme, isRtl, align, isHeader }) => ({
  ...(isRtl && {
    textAlign: align || 'right',
    direction: 'rtl',
  }),
  ...(isHeader && {
    fontWeight: 'bold',
  }),
}));

// RTL Dialog Title
const RTLDialogTitle = styled(DialogTitle)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

// RTL Dialog Content
const RTLDialogContent = styled(DialogContent)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

// RTL Dialog Actions
const RTLDialogActions = styled(DialogActions)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
}));

// Status chip colors
const statusColors = {
  ASSIGNED: 'default',
  ACCEPTED: 'info',
  IN_PROGRESS: 'primary',
  COMPLETED: 'success',
  REJECTED: 'error',
  FORWARDED: 'warning',
  REJECTED_RECEIPT: 'error',
  SKIPPED: 'secondary',
};

// Priority colors
const priorityColors = {
  LOW: 'success',
  MEDIUM: 'info',
  HIGH: 'warning',
  URGENT: 'error',
};

// Define the task API hooks
const useMyTasks = (filters, token) => {
  const apiFilters = {
    status: filters.status || '',
    search: filters.search || '',
    document_type: filters.document_type,
    from_date: filters.from_date,
    to_date: filters.to_date,
    page: filters.page || 1,
    limit: filters.limit || 10
  };
  
  return useFetchObjects('task-assignments/my-assignments', apiFilters, token);
};



const useAssignedByMeTasks = (filters, token) => {
  const apiFilters = {
    status: filters.status || '',
    search: filters.search || '',
    document_type: filters.document_type,
    from_date: filters.from_date,
    to_date: filters.to_date,
    page: filters.page || 1,
    limit: filters.limit || 10
  };
  
  return useFetchObjects('task-assignments/assigned-by-me', apiFilters, token);
};

const useAllTasks = (filters, token) => {
  // Return empty data instead of making the API call that was causing 500 errors
  // Previously this was calling useFetchObjects('task-assignments', apiFilters, token)
  return {
    data: { data: [], total: 0, page: 1, limit: 10, totalPages: 0 },
    isLoading: false,
    isError: false,
    error: null,
    refetch: () => console.log('All tasks view is disabled')
  };
};

// Loading overlay component
const LoadingOverlay = ({ loading }) => {
  if (!loading) return null;
  
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        zIndex: 1000,
      }}
    >
      <CircularProgress />
    </Box>
  );
};

// Mobile Task Card Component
const TaskMobileCard = ({ task, index, viewMode, onView, onAccept, onReject, onApprove, onForward, t }) => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isRTL = ['dari','ps'].includes(i18n.language);
  const document = task.document || {};
  const assignee = task.assignee || {};
  const assigner = task.assigner || {};
  
  const effectiveStatus = getEffectiveStatus(task);
  const isPastDue = task.due_date && new Date(task.due_date) < new Date();
  
  const statusColors = {
    'ASSIGNED': 'default',
    'ACCEPTED': 'primary',
    'IN_PROGRESS': 'primary',
    'COMPLETED': 'success',
    'REJECTED': 'error',
    'FORWARDED': 'warning',
    'REJECTED_RECEIPT': 'error'
  };

  const priorityColors = {
    'HIGH': 'error',
    'MEDIUM': 'warning',
    'LOW': 'success',
    'عاجل': 'error',
    'اطمینانیه': 'warning',
    'ابلاغیه': 'info',
    'محرم': 'secondary',
    'عادي': 'success'
  };

  const statusIcons = {
    'ASSIGNED': <AssignmentIcon fontSize="small" />,
    'ACCEPTED': <AcceptIcon fontSize="small" />,
    'IN_PROGRESS': <PersonIcon fontSize="small" />,
    'COMPLETED': <CheckCircleIcon fontSize="small" />,
    'REJECTED': <RejectIcon fontSize="small" />,
    'FORWARDED': <ForwardToInboxIcon fontSize="small" />,
    'REJECTED_RECEIPT': <RejectIcon fontSize="small" />
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'ASSIGNED': t('assigned_by_me:ASSIGNED'),
      'ACCEPTED': t('assigned_by_me:ACCEPTED'),
      'IN_PROGRESS': t('assigned_by_me:IN_PROGRESS'),
      'COMPLETED': t('assigned_by_me:COMPLETED'),
      'REJECTED': t('assigned_by_me:REJECTED'),
      'FORWARDED': t('assigned_by_me:FORWARDED'),
      'REJECTED_RECEIPT': t('assigned_by_me:REJECTED_RECEIPT')
    };
    return statusMap[status] || status;
  };

  const getPriorityColor = (priority) => {
    return priorityColors[priority] || 'default';
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (document.id) {
      window.open(`${process.env.REACT_APP_API_BASE_URL}/documents/${document.id}/download`, '_blank');
    }
  };

  return (
    <Card
      sx={{
        mb: 3,
        borderRadius: 3,
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      {/* Header with gradient background */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          p: 2,
          position: 'relative',
        }}
      >
        {/* Status Chip - Absolute positioned */}
        <Chip
          icon={statusIcons[effectiveStatus]}
          label={getStatusLabel(effectiveStatus)}
          size="small"
          color={statusColors[effectiveStatus] || 'default'}
          sx={{
            position: 'absolute',
            top: 8,
            right: isRTL ? 'auto' : 8,
            left: isRTL ? 8 : 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            color: theme.palette[statusColors[effectiveStatus]]?.main || 'text.primary',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
            '& .MuiChip-icon': {
              color: 'inherit',
            },
          }}
        />

        {/* Document Number and Title */}
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1.1rem',
              lineHeight: 1.2,
              mb: 0.5,
              color: 'white',
              textAlign: isRTL ? 'right' : 'left',
            }}
          >
            {document.reference_number || `#${document.id}`}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: '0.9rem',
              color: 'rgba(255, 255, 255, 0.9)',
              textAlign: isRTL ? 'right' : 'left',
              lineHeight: 1.3,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {document.title || t('tasks:no_document')}
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <CardContent sx={{ p: 3, pt: 2 }}>
        {/* Quick Info Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              {t('documents:documentType')}
            </Typography>
            <Chip
              label={document.type || '-'}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem', height: 20 }}
            />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              {t('documents:documentPriority')}
            </Typography>
            <Chip
              label={document.priority || '-'}
              size="small"
              color={getPriorityColor(document.priority)}
              sx={{ fontSize: '0.75rem', height: 20 }}
            />
          </Box>
        </Box>

        {/* Task Details Stack */}
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {/* Assignee/Assigner */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.8rem',
                bgcolor: viewMode === 'assigned-by-me' ? 
                  `${theme.palette.secondary.main}` : 
                  `${theme.palette.info.main}`,
              }}
            >
              {viewMode === 'assigned-by-me' ? 
                (assignee.full_name || assignee.username || 'U').charAt(0).toUpperCase() :
                (assigner.full_name || assigner.username || 'U').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                {viewMode === 'assigned-by-me' ? t('assigned_by_me:assignee') : t('assigned_by_me:assigned_by')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                {viewMode === 'assigned-by-me' ? 
                  assignee.full_name || assignee.username || '-' :
                  assigner.full_name || assigner.username || '-'}
              </Typography>
            </Box>
          </Box>

          {/* Due Date */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimeIcon fontSize="small" color={isPastDue ? "error" : "action"} />
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
                {t('assigned_by_me:due_date')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: isPastDue ? 'error.main' : 'text.primary',
                }}
              >
                <LocalizedDate date={task.due_date} variant="body2" />
              </Typography>
            </Box>
          </Box>

          {/* Reference Number */}
          <Box sx={{ 
            backgroundColor: 'grey.50',
            borderRadius: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'grey.200',
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              {t('documents:referenceNumber')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
              {document.reference_number || '-'}
            </Typography>
          </Box>
        </Stack>

        {/* Additional Info Row */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 1,
            mb: 2,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
              {t('documents:number_of_pages')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {document.number_of_pages || '-'}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
              {t('documents:sourceType')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {document.source_type || '-'}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
              {t('assigned_by_me:status')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {getStatusLabel(effectiveStatus)}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      {/* Actions */}
      <CardActions
        sx={{
          p: 2,
          pt: 1,
          backgroundColor: 'grey.50',
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 1,
          flexDirection: isRTL ? 'row-reverse' : 'row',
        }}
      >
        <Button
          variant="contained"
          startIcon={!isRTL && <ViewIcon />}
          endIcon={isRTL && <ViewIcon />}
          onClick={() => onView(task)}
          size="small"
          color="primary"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            px: 2,
            py: 1,
            flex: 1,
          }}
        >
          {t('common:view')}
        </Button>

        {document.attachments_count > 0 && (
          <Button
            variant="outlined"
            startIcon={!isRTL && <DownloadIcon />}
            endIcon={isRTL && <DownloadIcon />}
            onClick={handleDownload}
            size="small"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 2,
              py: 1,
              minWidth: 'auto',
            }}
          >
            {document.attachments_count}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

function getEffectiveStatus(task) {
  if (!task) return '';
  if (task.effective_status) return task.effective_status;
  if (task.status === 'FORWARDED' && task.metadata?.forwardedCompletion) {
    return task.metadata.forwardedCompletion.status || task.status;
  }
  return task.status;
}

// TaskIndex main component
const TaskIndex = () => {
  // Ref for search debounce timer
  const searchTimer = React.useRef(null);
  
  const { t, i18n } = useTranslation(['assigned_by_me', 'workflows', 'documents', 'common', 'calendar']);
  const { user } = useAuth();
  const token = user?.token;
  const { isRTL, getRTLProps } = useRTL();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { dateFormat } = useDateFormat();
  
  // State for users list for filters - moved after token is defined
  const { users, loading: usersLoading, fetchUsers } = useLazyUsers(token);
  
  // View mode and tab state
  const [viewMode, setViewMode] = useState('assigned-by-me'); // 'assigned-by-me', 'my', or 'all'
  
  // Initialize active tab based on view mode: Show "ASSIGNED" tab (index 0) by default for assigned-by-me and my tasks view
  const getDefaultTabForMode = (mode) => {
    // For "زما لخوا استول شوی اسناد" (assigned-by-me) and "ماته رالیږل شوی اسناد" (my), show "ASSIGNED" tab by default
    return mode === 'assigned-by-me' || mode === 'my' ? 0 : 5;
  };
  
  const location = useLocation();
  const queryTab = new URLSearchParams(location.search).get('tab');
  const isAssignIntent = new URLSearchParams(location.search).get('assign') === '1';
  const initialTab = queryTab === 'in-progress' ? 1 : getDefaultTabForMode(viewMode);
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Task data state
  const [tasks, setTasks] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [totalTasks, setTotalTasks] = useState(0);
  
  // Selected task for dialogs
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  
  // Assignment dialogs
  const [assignTaskOpen, setAssignTaskOpen] = useState(false);
  const [assignTaskData, setAssignTaskData] = useState({ documentId: '' });
  const [documentSelectionOpen, setDocumentSelectionOpen] = useState(false);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    document_type: '',
    priority: '',
    assigned_by_id: '',
    from_date: null,
    to_date: null,
    assignee_id: '',
    page: currentPage,
    limit: pageSize
  });
  
  // API hooks
  // Suppress toast for update operations to prevent duplicate messages
  const { handleUpdate: updateTask } = useUpdate('task-assignments', token, null, null, null, false);
  // Suppress toast for activity creation to avoid duplicate notifications
  const { handleAdd: createActivity } = useAdd('document-activities', token, null, null, null, false);
  const updateWorkflowStep = async () => Promise.resolve();
  const { handleUpdate: updateWorkflowApi } = useUpdate('document-workflows', token, null, null, null, false);
  const { handleAdd: addTask } = useAdd('task-assignments', token);
  
  // Direct assign functionality hooks
  const { data: quickDocumentsData, refetch: refetchQuickDocuments } = useFetchObjects('documents', { limit: 1 }, token);
  
  // Load tasks based on view mode
  const loadTasks = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      // Create a copy of filters for the API request
      const apiFilters = {
        ...filters,
        page: currentPage,
        limit: pageSize
      };
      
      // Remove null/undefined values from filters, but keep empty strings for "All" selections
      Object.keys(apiFilters).forEach(key => {
        if (apiFilters[key] === null || apiFilters[key] === undefined) {
          delete apiFilters[key];
        }
      });
      
      // For specific filters, only send non-empty values
      if (apiFilters.document_type === '') {
        delete apiFilters.document_type;
      }
      if (apiFilters.assigned_by_id === '') {
        delete apiFilters.assigned_by_id;
      }
      if (apiFilters.search === '') {
        delete apiFilters.search;
      }
      if (apiFilters.priority === '') {
        delete apiFilters.priority;
      }
      
      // Handle date filters more accurately
      if (apiFilters.from_date && apiFilters.from_date !== '' && apiFilters.from_date !== null) {
        try {
          // If it's already in YYYY-MM-DD format, use it directly
          if (typeof apiFilters.from_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(apiFilters.from_date)) {
            // Already in correct format, keep as is
          } else {
            // Convert to YYYY-MM-DD format
            apiFilters.from_date = format(new Date(apiFilters.from_date), 'yyyy-MM-dd');
          }
        } catch (error) {
          console.warn('Invalid from_date format:', apiFilters.from_date);
          delete apiFilters.from_date;
        }
      } else {
        delete apiFilters.from_date;
      }
      
      if (apiFilters.to_date && apiFilters.to_date !== '' && apiFilters.to_date !== null) {
        try {
          // If it's already in YYYY-MM-DD format, use it directly
          if (typeof apiFilters.to_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(apiFilters.to_date)) {
            // Already in correct format, keep as is
          } else {
            // Convert to YYYY-MM-DD format
            apiFilters.to_date = format(new Date(apiFilters.to_date), 'yyyy-MM-dd');
          }
        } catch (error) {
          console.warn('Invalid to_date format:', apiFilters.to_date);
          delete apiFilters.to_date;
        }
      } else {
        delete apiFilters.to_date;
      }
      
      // Add status filter based on active tab
      const statusFilterMap = {
        0: 'ASSIGNED',
        1: 'IN_PROGRESS', // This will include both IN_PROGRESS and ACCEPTED
        2: 'COMPLETED',
        3: 'REJECTED',
        4: null // All tasks - no status filter (null will be deleted)
      };
      
      const statusFilter = statusFilterMap[activeTab];
      if (statusFilter && statusFilter !== '') {
        apiFilters.status = statusFilter;
      } else {
        delete apiFilters.status;
      }
      
      // Debug: Log the filters being sent to API
      console.log('API Filters being sent:', apiFilters);
      console.log('Document type filter:', apiFilters.document_type);
      console.log('Active tab:', activeTab);
      console.log('Status filter:', statusFilter);
      console.log('Full filters object:', filters);
      console.log('Final API filters:', apiFilters);
      
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Use the correct API endpoint for assigned-by-me tasks
          response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/task-assignments/assigned-by-me`, {
            params: {
              ...apiFilters,
              exclude_forwarded_only: true
            },
            headers
          });
      
      if (response?.data?.success) {
        let tasksData = response.data.data || [];
        
        // Debug: Log the received data
        console.log('Received tasks data:', tasksData.length, 'tasks');
        console.log('Document types in received data:', [...new Set(tasksData.map(task => task.document?.type))]);
        console.log('Full API response:', response.data);
        
        // For tab 1 (IN_PROGRESS), we need to include both IN_PROGRESS and ACCEPTED statuses
        if (activeTab === 1) {
          tasksData = tasksData.filter(task => 
            (task.effective_status || task.status) === 'IN_PROGRESS' || (task.effective_status || task.status) === 'ACCEPTED'
          );
        }
        
        // Filter out duplicate forwarded tasks
        const uniqueTasks = new Map();
        
        tasksData.forEach(task => {
          // For forwarded tasks, check if we already have the original task
          if (task.metadata?.originalTaskId) {
            const existingTask = uniqueTasks.get(task.metadata.originalTaskId);
            if (!existingTask) {
              uniqueTasks.set(task.id, task);
            }
          } else {
            // For non-forwarded tasks, just add them
            uniqueTasks.set(task.id, task);
          }
        });
        
        tasksData = Array.from(uniqueTasks.values());
        
        setTasks(tasksData);
        setTotalTasks(response.data.total || 0);
        
        // Update status counts (backend now handles forwarded tasks properly)
        const statusCounts = response.data.status_counts || {};
        setStatusCounts(statusCounts);
      } else {
        setError(response?.data?.message || 'Failed to load tasks');
      }
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError(err.response?.data?.message || 'An error occurred while loading tasks');
    } finally {
      setLoading(false);
    }
  };
  
    // Delete hook positioned after loadTasks to avoid TDZ
  const { handleDelete: handleDeleteTask, ConfirmDialog: DeleteConfirmDialog } = useDelete('task-assignments', token, loadTasks);

  // Task handlers
  const handleTaskAccept = createTaskAcceptHandler(
    updateTask,
    createActivity,
    updateWorkflowStep,
    user,
    t,
    loadTasks,
    setTaskDetailOpen
  );
  
  const handleTaskReject = createTaskRejectHandler(
    updateTask,
    createActivity,
    updateWorkflowStep,
    user,
    t,
    loadTasks,
    setRejectDialogOpen
  );
  
  const handleTaskApprove = createTaskApproveHandler(
    updateTask,
    createActivity,
    updateWorkflowStep,
    updateWorkflowApi,
    user,
    t,
    loadTasks,
    setApproveDialogOpen
  );
  
  // Load tasks on filter or tab changes
  useEffect(() => {
    loadTasksWithFilters(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, activeTab, currentPage, pageSize]);
  
  // Handler functions
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };
  
  const handlePageSizeChange = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1); // Reset to first page
  };
  
  const handleFilterChange = (field, value) => {
    const updatedFilters = {
      ...filters,
      [field]: value
    };
    
    setFilters(updatedFilters);
    
    // Immediate filtering for dropdown selections and dates
    if (field !== 'search') {
      setCurrentPage(1);
      // Use the updated filters immediately
      loadTasksWithFilters(updatedFilters);
    } else {
      // Debounce only for search field
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
      
      searchTimer.current = setTimeout(() => {
        setCurrentPage(1);
        loadTasksWithFilters(updatedFilters);
      }, 300); // Reduced debounce time for faster response
    }
  };
  
  // New function to load tasks with specific filters
  const loadTasksWithFilters = async (filterValues) => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      // Create a copy of filters for the API request
      const apiFilters = {
        ...filterValues,
        page: currentPage,
        limit: pageSize
      };
      
      // Remove null/undefined values from filters, but keep empty strings for "All" selections
      Object.keys(apiFilters).forEach(key => {
        if (apiFilters[key] === null || apiFilters[key] === undefined) {
          delete apiFilters[key];
        }
      });
      
      // For specific filters, only send non-empty values
      if (apiFilters.document_type === '') {
        delete apiFilters.document_type;
      }
      if (apiFilters.assignee_id === '') {
        delete apiFilters.assignee_id;
      }
      if (apiFilters.search === '') {
        delete apiFilters.search;
      }
      if (apiFilters.priority === '') {
        delete apiFilters.priority;
      }
      
      // Handle date filters more accurately
      if (apiFilters.from_date && apiFilters.from_date !== '' && apiFilters.from_date !== null) {
        try {
          // If it's already in YYYY-MM-DD format, use it directly
          if (typeof apiFilters.from_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(apiFilters.from_date)) {
            // Already in correct format, keep as is
          } else {
            // Convert to YYYY-MM-DD format
            apiFilters.from_date = format(new Date(apiFilters.from_date), 'yyyy-MM-dd');
          }
        } catch (error) {
          console.warn('Invalid from_date format:', apiFilters.from_date);
          delete apiFilters.from_date;
        }
      } else {
        delete apiFilters.from_date;
      }
      
      if (apiFilters.to_date && apiFilters.to_date !== '' && apiFilters.to_date !== null) {
        try {
          // If it's already in YYYY-MM-DD format, use it directly
          if (typeof apiFilters.to_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(apiFilters.to_date)) {
            // Already in correct format, keep as is
          } else {
            // Convert to YYYY-MM-DD format
            apiFilters.to_date = format(new Date(apiFilters.to_date), 'yyyy-MM-dd');
          }
        } catch (error) {
          console.warn('Invalid to_date format:', apiFilters.to_date);
          delete apiFilters.to_date;
        }
      } else {
        delete apiFilters.to_date;
      }
      
      // Add status filter based on active tab
      const statusFilterMap = {
        0: 'ASSIGNED',
        1: 'IN_PROGRESS', // This will include both IN_PROGRESS and ACCEPTED
        2: 'COMPLETED',
        3: 'REJECTED',
        4: null // All tasks - no status filter (null will be deleted)
      };
      
      const statusFilter = statusFilterMap[activeTab];
      if (statusFilter && statusFilter !== '') {
        apiFilters.status = statusFilter;
      } else {
        delete apiFilters.status;
      }
      
      // Debug: Log the filters being sent to API
      console.log('API Filters being sent:', apiFilters);
      console.log('Document type filter:', apiFilters.document_type);
      console.log('Active tab:', activeTab);
      console.log('Status filter:', statusFilter);
      console.log('Full filters object:', filterValues);
      console.log('Final API filters:', apiFilters);
      
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Use the correct API endpoint for assigned-by-me tasks
      response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/task-assignments/assigned-by-me`, {
        params: {
          ...apiFilters,
          exclude_forwarded_only: true
        },
        headers
      });
      
      if (response?.data?.success) {
        let tasksData = response.data.data || [];
        
        // Debug: Log the received data
        console.log('Received tasks data:', tasksData.length, 'tasks');
        console.log('Document types in received data:', [...new Set(tasksData.map(task => task.document?.type))]);
        console.log('Full API response:', response.data);
        
        // For tab 1 (IN_PROGRESS), we need to include both IN_PROGRESS and ACCEPTED statuses
        if (activeTab === 1) {
          tasksData = tasksData.filter(task => 
            (task.effective_status || task.status) === 'IN_PROGRESS' || (task.effective_status || task.status) === 'ACCEPTED'
          );
        }
        
        // Filter out duplicate forwarded tasks
        const uniqueTasks = new Map();
        
        tasksData.forEach(task => {
          // For forwarded tasks, check if we already have the original task
          if (task.metadata?.originalTaskId) {
            const existingTask = uniqueTasks.get(task.metadata.originalTaskId);
            if (!existingTask) {
              uniqueTasks.set(task.id, task);
            }
          } else {
            // For non-forwarded tasks, just add them
            uniqueTasks.set(task.id, task);
          }
        });
        
        tasksData = Array.from(uniqueTasks.values());
        
        setTasks(tasksData);
        setTotalTasks(response.data.total || 0);
        
        // Update status counts (backend now handles forwarded tasks properly)
        const statusCounts = response.data.status_counts || {};
        setStatusCounts(statusCounts);
      } else {
        setError(response?.data?.message || 'Failed to load tasks');
      }
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError(err.response?.data?.message || 'An error occurred while loading tasks');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadTasksWithFilters(filters);
  };
  
  const handleResetFilters = () => {
    const resetFilters = {
      status: '',
      search: '',
      document_type: '',
      priority: '',
      assigned_by_id: '',
      from_date: null,
      to_date: null,
      assignee_id: '',
      page: 1,
      limit: pageSize
    };
    setFilters(resetFilters);
    setCurrentPage(1);
    loadTasksWithFilters(resetFilters);
  };
  
  const toggleViewMode = (mode) => {
    if (mode !== viewMode) {
      // If switching to assigned-by-me and current tab is FORWARDED (tab index 4),
      // change to All tab (which would be index 4 for assigned-by-me)
      if (mode === 'assigned-by-me' && activeTab === 4) {
        setActiveTab(4); // Move to 'All' tab for the new view mode
      } else if (activeTab === 5 && mode === 'assigned-by-me') {
        // If current tab is 'All' (index 5) and moving to assigned-by-me where 'All' is index 4
        setActiveTab(4);
      } else {
        // Use the getDefaultTabForMode helper to select the appropriate default tab
        setActiveTab(getDefaultTabForMode(mode));
      }
      setViewMode(mode);
      setCurrentPage(1);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setCurrentPage(1);
  };
  
  const handleViewTask = (task) => {
    setSelectedTask(task);
    setTaskDetailOpen(true);
  };
  
  const handleOpenRejectDialog = (task) => {
    setSelectedTask(task);
    setRejectDialogOpen(true);
  };
  
  const handleOpenApproveDialog = (task) => {
    setSelectedTask(task);
    setApproveDialogOpen(true);
  };
  
  const handleForwardTask = async (task, forwardToId, reason, departmentId, metadata) => {
    try {
      // Create the forward request data
      const forwardData = {
        forward_to_id: forwardToId,
        department_id: departmentId,
        reason: reason
      };
      
      // Use the dedicated forward API endpoint
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task-assignments/${task.id}/forward`,
        forwardData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Use the metadata if provided, or create a basic one
      const activityMetadata = metadata || {
        task_id: task.id,
        document_id: task.document_id || task.document?.id,
        user_id: user.id,
        user_name: user.full_name || user.username,
        action: 'FORWARDED',
        reason: reason,
        from: user.full_name || user.username,
        to: forwardToId ? 'another user' : departmentId ? 'a department' : 'unknown recipient'
      };
      
      // Create an activity record for the forwarding
      await createActivity({
        document_id: task.document_id || task.document?.id,
        user_id: user.id,
        activity_type: 'FORWARDED',
        description: `${user.full_name || user.username} forwarded the task to ${activityMetadata.to || 'another user/department'}`,
        step_id: task.workflow_step_id,
        metadata: activityMetadata
      });
      
      // Success toast is handled by useTaskOperations hook
      loadTasks();
      setTaskDetailOpen(false);
    } catch (err) {
      console.error('Error forwarding task:', err);
      toast.error(err.response?.data?.message || t('assigned_by_me:task_forward_error', 'Failed to forward task'));
    }
  };
  
  const handleOpenAssignTask = (documentId) => {
    setAssignTaskData({ documentId });
    setAssignTaskOpen(true);
  };
  
  const handleDirectAssign = async () => {
    if (!quickDocumentsData?.data || quickDocumentsData.data.length === 0) {
      toast.info('Please create a document first');
      return;
    }
    
    try {
      const documentId = quickDocumentsData.data[0].id;
      setDocumentSelectionOpen(true);
    } catch (error) {
      console.error('Error in direct assign:', error);
      toast.error('Failed to assign task directly');
    }
  };
  
  const handleDocumentSelected = (documentId) => {
    setDocumentSelectionOpen(false);
    if (documentId) {
      setAssignTaskData({ documentId });
      setAssignTaskOpen(true);
    }
  };
  
  const handleExportTasks = () => {
    const fileName = `tasks_export_${new Date().toISOString().split('T')[0]}.csv`;
    const tasksCSV = [
      ['Document ID', 'Document Title', 'Document Type', 'Assignee', 'Status', 'Priority', 'Due Date', 'Assigned At', 'Completed At'],
      ...tasks.map(task => [
        task.document?.id || '',
        task.document?.title || '',
        task.document?.type || '',
        task.assignee?.full_name || task.assignee?.username || '',
        task.status,
        task.priority,
        safeFormatDate(task.due_date, 'yyyy-MM-dd') || '',
        safeFormatDate(task.assigned_at, 'yyyy-MM-dd') || '',
        safeFormatDate(task.completed_at, 'yyyy-MM-dd') || ''
      ])
    ];
    
    const csvContent = tasksCSV.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Open assign dialog automatically if ?assign=1 is present in URL
  useEffect(() => {
    if (isAssignIntent) {
      setDocumentSelectionOpen(true);
    }
  }, [isAssignIntent]);

  // Calculate statistics
  const totalPending = (statusCounts.ASSIGNED || 0) + (statusCounts.ACCEPTED || 0) + (statusCounts.IN_PROGRESS || 0);
  const totalCompleted = statusCounts.COMPLETED || 0;
  const totalRejected = statusCounts.REJECTED || 0;
  const totalForwarded = statusCounts.FORWARDED || 0;

  const [searchParams, setSearchParams] = useSearchParams();

  const handleCloseTaskDetail = () => {
    setTaskDetailOpen(false);
    if (searchParams.get('task')) {
      searchParams.delete('task');
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Tab value to status mapping (dynamically adjusted based on view mode)
  const getTabToStatus = useCallback((viewMode) => {
    if (viewMode === 'assigned-by-me') {
      // For assigned-by-me view, we skip the FORWARDED tab
      return {
        0: t('assigned_by_me:assigned'),
        1: t('assigned_by_me:in_progress'),
        2: t('assigned_by_me:completed'),
        3: t('assigned_by_me:rejected'),
        4: ''  // All tasks (moved up one position)
      };
    } else {
      // Regular tab mapping for other views
      return {
        0: t('assigned_by_me:assigned'),
        1: t('assigned_by_me:in_progress'),
        2: t('assigned_by_me:completed'),
        3: t('assigned_by_me:rejected'),
        4: t('assigned_by_me:forwarded'),
        5: ''  // All tasks (at the end)
      };
    }
  }, [t]);

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ width: '100%', p: { xs: 0.5, sm: 3 } }}>
      {/* Page Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        flexDirection: isMobile ? 'column' : (isRTL ? 'row-reverse' : 'row'),
        gap: isMobile ? 2 : 0,
        width: '100%',
      }}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
          <RTLButton
            variant="contained"
            color="primary"
            startIcon={!isRTL && <AddIcon />}
            endIcon={isRTL && <AddIcon />}
            onClick={() => setDocumentSelectionOpen(true)}
            {...getRTLProps('button')}
            sx={{ 
              whiteSpace: 'nowrap',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              minHeight: '48px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                transform: 'translateY(-1px)'
              },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {t('assigned_by_me:assign_new_task')}
          </RTLButton>
          
          <RTLButton
            variant="outlined"
            startIcon={!isRTL && <AssignmentIcon />}
            endIcon={isRTL && <AssignmentIcon />}
            onClick={() => setBulkAssignOpen(true)}
            {...getRTLProps('button')}
            sx={{ 
              whiteSpace: 'nowrap',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              minHeight: '48px',
              textTransform: 'none',
              fontWeight: 600,
              border: '2px solid',
                borderColor: 'primary.main',
              '&:hover': {
                borderColor: 'primary.dark',
                backgroundColor: 'primary.50',
                transform: 'translateY(-1px)'
              },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              transition: 'all 0.2s ease-in-out'
            }}
          >
            {t('tasks:bulk_assign')}
          </RTLButton>
        </Box>
        
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            textAlign: isMobile ? 'center' : (isRTL ? 'right' : 'left'),
            fontWeight: 700,
            background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.5px',
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
            order: isMobile ? -1 : 0
          }}
        >
          {t('assigned_by_me:task_management')}
        </Typography>
      </Box>
      
      {/* Status Cards (replacing tabs) */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card 
              sx={{
                borderRadius: { xs: 2, sm: 3 }, 
                boxShadow: activeTab === 0 ? '0 8px 24px rgba(25, 118, 210, 0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                border: '2px solid',
                borderColor: activeTab === 0 ? 'primary.main' : 'divider',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                backgroundColor: activeTab === 0 ? 'primary.50' : 'background.paper',
                height: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  borderColor: 'primary.main'
                }
              }}
              onClick={() => handleTabChange(null, 0)}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                <Box sx={{ 
                  width: { xs: 32, sm: 40 }, 
                  height: { xs: 32, sm: 40 }, 
                  borderRadius: '50%', 
                  bgcolor: activeTab === 0 ? 'primary.main' : 'secondary.100', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 0.5, sm: 1 }
                }}>
                  <AssignmentIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: activeTab === 0 ? 'white' : 'secondary.main' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} color={activeTab === 0 ? 'primary.main' : 'text.primary'} gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {statusCounts.ASSIGNED || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.2 }}>
                  {t('assigned_by_me:ASSIGNED')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2.4}>
            <Card 
              sx={{ 
                borderRadius: { xs: 2, sm: 3 }, 
                boxShadow: activeTab === 1 ? '0 8px 24px rgba(25, 118, 210, 0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                border: '2px solid',
                borderColor: activeTab === 1 ? 'primary.main' : 'divider',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                backgroundColor: activeTab === 1 ? 'primary.50' : 'info.100',
                height: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  borderColor: 'primary.main'
                }
              }}
              onClick={() => handleTabChange(null, 1)}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                <Box sx={{ 
                  width: { xs: 32, sm: 40 }, 
                  height: { xs: 32, sm: 40 }, 
                  borderRadius: '50%', 
                  bgcolor: activeTab === 1 ? 'primary.main' : 'info.100', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 0.5, sm: 1 }
                }}>
                  <PersonIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: activeTab === 1 ? 'white' : 'info.main' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} color={activeTab === 1 ? 'primary.main' : 'text.primary'} gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {(statusCounts.IN_PROGRESS || 0) + (statusCounts.ACCEPTED || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.2 }}>
                  {t('assigned_by_me:IN_PROGRESS')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2.4}>
            <Card 
              sx={{ 
                borderRadius: { xs: 2, sm: 3 }, 
                boxShadow: activeTab === 2 ? '0 8px 24px rgba(25, 118, 210, 0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                border: '2px solid',
                borderColor: activeTab === 2 ? 'primary.main' : 'divider',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                backgroundColor: activeTab === 2 ? 'primary.50' : 'success.100',
                height: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  borderColor: 'primary.main'
                }
              }}
              onClick={() => handleTabChange(null, 2)}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                <Box sx={{ 
                  width: { xs: 32, sm: 40 }, 
                  height: { xs: 32, sm: 40 }, 
                  borderRadius: '50%', 
                  bgcolor: activeTab === 2 ? 'primary.main' : 'success.100', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 0.5, sm: 1 }
                }}>
                  <CheckCircleIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: activeTab === 2 ? 'white' : 'success.main' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} color={activeTab === 2 ? 'primary.main' : 'text.primary'} gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {statusCounts.COMPLETED || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.2 }}>
                  {t('assigned_by_me:COMPLETED')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={6} sm={4} md={2.4}>
            <Card 
              sx={{ 
                borderRadius: { xs: 2, sm: 3 }, 
                boxShadow: activeTab === 3 ? '0 8px 24px rgba(25, 118, 210, 0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                border: '2px solid',
                borderColor: activeTab === 3 ? 'primary.main' : 'divider',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                backgroundColor: activeTab === 3 ? 'primary.50' : 'error.100',
                height: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  borderColor: 'primary.main'
                }
              }}
              onClick={() => handleTabChange(null, 3)}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                <Box sx={{ 
                  width: { xs: 32, sm: 40 }, 
                  height: { xs: 32, sm: 40 }, 
                  borderRadius: '50%', 
                  bgcolor: activeTab === 3 ? 'primary.main' : 'error.100', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 0.5, sm: 1 }
                }}>
                  <RejectIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: activeTab === 3 ? 'white' : 'error.main' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} color={activeTab === 3 ? 'primary.main' : 'text.primary'} gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {statusCounts.REJECTED || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.2 }}>
                  {t('assigned_by_me:REJECTED')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={12} md={2.4}>
            <Card 
              sx={{ 
                borderRadius: { xs: 2, sm: 3 }, 
                boxShadow: activeTab === 4 ? '0 8px 24px rgba(25, 118, 210, 0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                border: '2px solid',
                borderColor: activeTab === 4 ? 'primary.main' : 'divider',
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                backgroundColor: activeTab === 4 ? 'primary.50' : 'grey.100',
                height: { xs: 'auto', sm: 'auto' },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  borderColor: 'primary.main'
                }
              }}
              onClick={() => handleTabChange(null, 4)}
            >
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                <Box sx={{ 
                  width: { xs: 32, sm: 40 }, 
                  height: { xs: 32, sm: 40 }, 
                  borderRadius: '50%', 
                  bgcolor: activeTab === 4 ? 'primary.main' : 'grey.100', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: { xs: 0.5, sm: 1 }
                }}>
                  <ListIcon sx={{ fontSize: { xs: 16, sm: 20 }, color: activeTab === 4 ? 'white' : 'grey.600' }} />
                </Box>
                <Typography variant="h6" fontWeight={700} color={activeTab === 4 ? 'primary.main' : 'text.primary'} gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {totalTasks || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' }, lineHeight: 1.2 }}>
                  {t('assigned_by_me:all')}
                </Typography>
              </CardContent>
        </Card>
          </Grid>
        </Grid>
      </Box>
      
      {/* Search and filters */}
      <Card sx={{ mb: 3, boxShadow: 1 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 0.5, sm: 2 },
            flexDirection: isRTL ? 'row-reverse' : 'row',
            width: '100%',
          }}>
        <RTLTextField
          variant="outlined"
          size="small"
          placeholder={t('common:search')}
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          sx={{ 
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              paddingRight: isRTL ? '0px' : '0',
              paddingLeft: isRTL ? '0' : '0px',
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main'
                }
              }
            },
            '& .MuiOutlinedInput-input': {
              padding: '14px 16px',
              fontSize: '0.95rem'
            }
          }}
          {...getRTLProps('textfield')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            dir: isRTL ? 'rtl' : 'ltr',
          }}
        />
        
        <RTLButton 
          variant="outlined" 
          startIcon={!isRTL && <DownloadIcon />}
          endIcon={isRTL && <DownloadIcon />}
          onClick={handleExportTasks}
          {...getRTLProps('button')}
          sx={{
            borderRadius: 2,
            px: 2,
            py: 1,
            minHeight: '48px',
            textTransform: 'none',
            fontWeight: 600,
            border: '2px solid',
            borderColor: 'success.main',
            color: 'success.main',
            minWidth: { xs: 'auto', sm: '100px' },
            '&:hover': {
              borderColor: 'success.dark',
              backgroundColor: 'success.50'
            }
          }}
        >
          {t('common:export')}
        </RTLButton>
        
        <RTLButton
          variant="outlined"
          color="primary"
          onClick={() => setShowFilters(!showFilters)}
          startIcon={!isRTL && <FilterAltIcon fontSize="small" />}
          endIcon={isRTL && <FilterAltIcon fontSize="small" />}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexDirection: isRTL ? 'row-reverse' : 'row',
            borderRadius: 2,
            px: 2,
            py: 1,
            minHeight: '48px',
            textTransform: 'none',
            fontWeight: 600,
            border: '2px solid',
            borderColor: 'primary.main',
            minWidth: { xs: 'auto', sm: '100px' },
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'primary.50'
            }
          }}
          {...getRTLProps('button')}
        >
          {showFilters ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          {t('common:filter')}
        </RTLButton>
          </Box>
        </CardContent>
      </Card>

      <Collapse in={showFilters}>
        <Card sx={{ mb: 3, boxShadow: 1 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 3, 
            width: '100%',
            '& .MuiFormControl-root': {
              width: '100%'
            },
            mb: 3
          }}>
            {/* Document Type Filter */}
            <RTLFormControl 
              size="small" 
              sx={{ mb: 2 }}
              {...getRTLProps('select')}
            >
              <InputLabel id="document-type-label" sx={{ fontWeight: 600 }}>
                {t('documents:type_filter')}
              </InputLabel>
              <Select
                labelId="document-type-label"
                value={filters.document_type}
                onChange={(e) => handleFilterChange('document_type', e.target.value)}
                label={t('documents:type_filter')}
                dir={isRTL ? 'rtl' : 'ltr'}
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <MenuItem value="">{t('common:all')}</MenuItem>
                <MenuItem value="مکتوب">مکتوب</MenuItem>
                <MenuItem value="پیشهاد">پیشهاد</MenuItem>
                <MenuItem value="استعلام">استعلام</MenuItem>
                <MenuItem value="گزارش">گزارش</MenuItem>
              </Select>
            </RTLFormControl>
            
            {/* Assigned To Filter */}
            <RTLFormControl 
              size="small" 
              sx={{ mb: 2 }}
              {...getRTLProps('select')}
            >
              <InputLabel id="assigned-to-label" sx={{ fontWeight: 600 }}>
                {t('assigned_by_me:assigned_to')}
              </InputLabel>
              <Select
                labelId="assigned-to-label"
                value={filters.assignee_id}
                onChange={(e) => handleFilterChange('assignee_id', e.target.value)}
                onOpen={() => {
                  // Trigger lazy loading when dropdown is opened
                  if (users.length === 0 && !usersLoading) {
                    fetchUsers();
                  }
                }}
                label={t('assigned_by_me:assigned_to')}
                dir={isRTL ? 'rtl' : 'ltr'}
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <MenuItem value="">{t('common:all')}</MenuItem>
                {usersLoading ? (
                  <MenuItem disabled>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress size={20} />
                      {t('common:loading')}
                    </Stack>
                  </MenuItem>
                ) : users.length === 0 ? (
                  <MenuItem disabled>{t('common:no_users_found', 'No users found')}</MenuItem>
                ) : (
                  users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.full_name || user.username}
                    </MenuItem>
                  ))
                )}
              </Select>
            </RTLFormControl>
            
            {/* Priority Filter */}
            <RTLFormControl 
              size="small" 
              sx={{ mb: 2 }}
              {...getRTLProps('select')}
            >
              <InputLabel id="priority-label" sx={{ fontWeight: 600 }}>
                {t('documents:priority')}
              </InputLabel>
              <Select
                labelId="priority-label"
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                label={t('documents:priority')}
                dir={isRTL ? 'rtl' : 'ltr'}
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'divider'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }}
              >
                <MenuItem value="">{t('common:all')}</MenuItem>
                <MenuItem value="عاجل">عاجل</MenuItem>
                <MenuItem value="اطمینانیه">اطمینانیه</MenuItem>
                <MenuItem value="ابلاغیه">ابلاغیه</MenuItem>
                <MenuItem value="محرم">محرم</MenuItem>
                <MenuItem value="عادي">عادي</MenuItem>
              </Select>
            </RTLFormControl>
            
            {/* Empty space for alignment */}
            <Box sx={{ mb: 2 }} />
          </Box>
          
          {/* Date Filters - Positioned below other filters */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 3, 
            width: '100%',
            mt: 2,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'divider',
            '& .MuiFormControl-root': {
              width: '100%'
            }
          }}>
            {/* From Date Filter */}
            <Box sx={{ mb: 2 }}>
              <LocalizedDatePicker
                label={t('assigned_by_me:from_date')}
                value={filters.from_date || ''}
                onChange={(e) => handleFilterChange('from_date', e.target.value || null)}
                name="from_date"
                {...getRTLProps('textfield')}
                inputProps={{
                  size: 'small',
                  sx: {
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }
                }}
              />
            </Box>
            
            {/* To Date Filter */}
            <Box sx={{ mb: 2 }}>
              <LocalizedDatePicker
                label={t('assigned_by_me:to_date')}
                value={filters.to_date || ''}
                onChange={(e) => handleFilterChange('to_date', e.target.value || null)}
                name="to_date"
                {...getRTLProps('textfield')}
                inputProps={{
                  size: 'small',
                  sx: {
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }
                }}
              />
            </Box>
          </Box>
          
          {/* Filter action buttons */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: isRTL ? 'flex-end' : 'flex-start', 
            gap: 2, 
            mt: 3,
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <RTLButton
              variant="outlined"
              color="secondary"
              onClick={handleResetFilters}
              {...getRTLProps('button')}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1,
                minHeight: '48px',
                textTransform: 'none',
                fontWeight: 600,
                border: '2px solid',
                borderColor: 'grey.400',
                color: 'grey.700',
                fontSize: '0.875rem',
                minWidth: '100px',
                '&:hover': {
                  borderColor: 'grey.600',
                  backgroundColor: 'grey.50'
                }
              }}
            >
              {t('common:reset')}
            </RTLButton>
          </Box>
          </CardContent>
        </Card>
      </Collapse>

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ 
          mb: 4, 
          textAlign: isRTL ? 'right' : 'left',
          borderRadius: 2,
          '& .MuiAlert-icon': {
            fontSize: '1.5rem'
          }
        }}>
          {error || t('common:error_occurred')}
        </Alert>
      )}
        
        {/* Task List */}
      <Box sx={{ position: 'relative', minHeight: '400px' }}>
        {loading && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1, 
            width: '100%'
          }}>
            <LinearProgress variant="indeterminate" sx={{ height: 4 }} />
          </Box>
        )}
        
        {tasks.length === 0 && !loading ? (
          <Card 
            sx={{ 
              p: 4, 
              textAlign: 'center', 
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Box sx={{ 
              width: 80, 
              height: 80, 
              borderRadius: '50%', 
              bgcolor: 'grey.100', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}>
              <AssignmentIcon sx={{ fontSize: 40, color: 'grey.400' }} />
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
              {t('assigned_by_me:no_tasks_found_message')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('assigned_by_me:try_different_filters_message')}
            </Typography>
          </Card>
        ) : (
          <Card 
            sx={{
              borderRadius: 2, 
              p: { xs: 1.5, sm: 3 },
              boxShadow: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ 
              mb: 3,
              p: 2,
              backgroundColor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 600 }}>
                {tasks.length} {tasks.length === 1 ? t('assigned_by_me:task_singular') : t('assigned_by_me:task_plural')} {getTabToStatus(viewMode)[activeTab] ? `• ${t(`assigned_by_me:${getTabToStatus(viewMode)[activeTab].toLowerCase()}`, getTabToStatus(viewMode)[activeTab])}` : ''}
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 3 
            }}>
            {tasks.map((task) => (
              isMobile ? (
                <TaskMobileCard
                  key={task.id}
                  task={task}
                  viewMode={viewMode}
                  onView={handleViewTask}
                  onAccept={handleTaskAccept}
                  onReject={handleOpenRejectDialog}
                  onApprove={handleOpenApproveDialog}
                  onForward={handleForwardTask}
                  t={t}
                />
              ) : (
                <TaskCard 
                  key={task.id}
                  task={task}
                  viewMode={viewMode}
                  onView={handleViewTask}
                  onDelete={handleDeleteTask}
                  onAccept={handleTaskAccept}
                  onReject={handleOpenRejectDialog}
                  onApprove={handleOpenApproveDialog}
                  onForward={handleForwardTask}
                  t={t}
                />
              )
            ))}
            </Box>
          </Card>
        )}
      </Box>
            
            {/* Pagination */}
      {tasks.length > 0 && (
        <Card sx={{ 
          p: { xs: 1, sm: 2 }, 
          borderRadius: 2, 
          mt: 2, 
          boxShadow: 1 
        }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: isRTL ? 'row-reverse' : 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 1, sm: 2 },
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'center', sm: 'flex-start' }
          }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                mr: isRTL ? 0 : { xs: 1, sm: 2 }, 
                ml: isRTL ? { xs: 1, sm: 2 } : 0,
                whiteSpace: 'nowrap',
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}
            >
              {t('common:rows_per_page')}
              {isRTL ? ':' : ':'} 
              </Typography>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              size="small"
              sx={{ 
                minWidth: { xs: 70, sm: 80 },
                '& .MuiSelect-select': {
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.5, sm: 1 },
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                },
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main'
                },
                ...(isRTL && {
                  '& .MuiSelect-select': {
                    paddingRight: { xs: '28px', sm: '32px' },
                    textAlign: 'right',
                    paddingLeft: { xs: '12px', sm: '14px' },
                  },
                  '& .MuiSelect-icon': {
                    right: { xs: 6, sm: 7 },
                    left: 'auto',
                  },
                }),
              }}
              MenuProps={{
                disableScrollLock: true,
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: isRTL ? 'right' : 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: isRTL ? 'right' : 'left',
                },
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={totalTasks}>All</MenuItem>
            </Select>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 2, sm: 3 },
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'center', sm: 'flex-end' }
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {t('common:showing')} {(currentPage - 1) * pageSize + 1}-
              {Math.min(currentPage * pageSize, totalTasks)} {t('common:of')} {totalTasks}
            </Typography>
            <Pagination
              count={Math.max(Math.ceil(totalTasks / pageSize), 1)}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              dir={isRTL ? 'rtl' : 'ltr'}
              shape="rounded"
              size="medium"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontFamily: isRTL ? 'inherit' : 'inherit',
                  fontWeight: 500,
                  borderRadius: 2,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  minWidth: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  '&:hover': {
                    backgroundColor: 'primary.50'
                  }
                },
                '& .Mui-selected': {
                  fontWeight: 600,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.2)'
                },
                '& .MuiPaginationItem-previousNext': {
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  minWidth: { xs: 28, sm: 36 },
                  height: { xs: 28, sm: 36 },
                },
                '& .MuiPaginationItem-firstLast': {
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  minWidth: { xs: 28, sm: 36 },
                  height: { xs: 28, sm: 36 },
                }
              }}
              getItemAriaLabel={(type) => {
                return t(`common:pagination.${type}`);
              }}
            />
            </Box>
          </Box>
        </Card>
        )}
        
      {/* Task Detail Dialog */}
        <TaskDetailDialog 
          task={selectedTask}
          open={taskDetailOpen}
          onClose={handleCloseTaskDetail}
          onSuccess={(task, action) => {
            // Handle all success cases
            switch(action) {
              case 'received': 
                loadTasks();
                break;
              case 'completed': 
                // Completion is handled by TaskDetailDialogV2 internally
                break;
              case 'rejected': 
                // Rejection is handled by TaskDetailDialogV2 internally
                break;
              case 'forwarded': 
                // Forwarding is handled by TaskDetailDialogV2 internally
                break;
            }
          }}
        />
      
      {/* Task Assignment Dialogs */}
        <TaskAssignForm
          open={assignTaskOpen}
          onClose={() => setAssignTaskOpen(false)}
          documentId={assignTaskData.documentId}
        onSuccess={loadTasks}
        isRtl={isRTL}
      />
      
        <DocumentSelectionDialog
          open={documentSelectionOpen}
          onClose={() => setDocumentSelectionOpen(false)}
          onDocumentSelected={handleDocumentSelected}
          {...getRTLProps('textfield')}
          excludedDocumentIds={tasks.map(task => task.document?.id).filter(Boolean)}
        />
        
        <BulkAssignForm
          open={bulkAssignOpen}
          onClose={() => setBulkAssignOpen(false)}
        onSuccess={loadTasks}
        isRtl={isRTL}
      />
      
      {/* Task Action Dialogs */}
      <RejectDialog 
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onReject={(reason) => handleTaskReject(selectedTask, reason)}
        isRtl={isRTL}
      />
      
      <ApproveDialog 
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onApprove={(remarks, attachment) => handleTaskApprove(selectedTask, remarks, attachment)}
        isRtl={isRTL}
        />
      <DeleteConfirmDialog />
      </Box>
  );
};

export default TaskIndex;
